/**
 * THE PHOTO SHELF — what a section's photos look like laid out first (D-028).
 *
 * Brief §3, 2026-08-30: *"This survey is photo-heavy by nature, but photos
 * are attached fields rather than the lead of each section. The camera should
 * be the first thing the thumb reaches."* So every section screen opens with
 * a shelf: one card per recommended shot, then every extra, and an add button
 * pinned in the header where no amount of photos can scroll it away.
 *
 * This module is the ARRANGEMENT and nothing else. It decides nothing about
 * got/missing — that is `photoCoversShot` (src/core/sections.ts), the one
 * definition the validation engine's `missingShots` and the close panel's
 * `sectionCloseView` already share, so a shot the shelf shows ticked is a
 * shot the record calls taken. It writes nothing: the shelf's component
 * captures through `usePhotoCapture`, the one capture path. A second opinion
 * on either would be how the shelf and the office start disagreeing about
 * which pictures exist.
 *
 * ── One photo, one card ─────────────────────────────────────────────────────
 * The section-level `PhotoCapture` grid used to list shot-tagged photos as
 * well as extras (its filter passed anything without a questionId), so a
 * recommended shot appeared twice on one screen — once on its row, once in
 * the section grid. `tests/e2e/offline.spec.ts` pins "one attach turns
 * exactly one row green". Here a photo is EITHER the cover of a prompted shot
 * OR an extra, never both; and a photo filed on a question is neither — it
 * lives in that question's drawer and is counted on the map, not shown here.
 *
 * ── The empty hand (round 7 task 5, 2026-09-03) ─────────────────────────────
 * The design authority: *"the picture carousel [should] appear on all survey
 * screens… If there aren't required pictures for a survey section, the
 * carousel should display a single empty picture labeled 'Photo 1.'"* So a
 * section whose shot list and extras both come up empty gets one
 * `PlaceholderCard` instead of an empty `cards` array — the shelf itself
 * never returns nothing. Building and Room Setup, the two screens Stephanie
 * named as exempt, never reach this module at all (`PhotoShelf.tsx` /
 * `SectionScreen.tsx` decide that, one layer up).
 *
 * Pure: no React, no store. `tests/unit/photo-shelf.test.ts` runs it directly.
 */

import { photoCoversShot, shotBaseId } from '../../core/sections';
import type { PhotoRef, SectionInstance, SurveyDocument } from '../../core/types';
import { shotReasonFor } from '../../core/validation';
import { FLS_SHOT_NOTES } from '../components/flsShots';
import type { SurveyResults } from './results';

/** One recommended shot's card — got (with its photo) or still a placeholder. */
export interface ShotCard {
  kind: 'shot';
  shotListId: string;
  label: string;
  /** Why this picture matters / what to frame — the ⓘ on the card. */
  note?: string;
  /** The covering photo, when there is one. */
  photo?: PhotoRef;
  /**
   * The card's BAND text (round 7 task 4, D-038) — the photo's own label when
   * the surveyor typed one, else the shot's own name. A todo card (no photo
   * yet) always shows the shot's name: there is nothing yet to carry a label.
   */
  title: string;
  /** True when the covering photo is parked (`Needs you`) — the band turns red. */
  parked: boolean;
  /** True when the covering photo is parked and cannot be re-sent (evicted). */
  gone?: boolean;
  /** Amendment 8 (D-041) — set on an UNCOVERED required shot's card. */
  required?: true;
  /** Amendment 8 (D-041) — the surveyor's own written reason, when there is one. */
  reason?: string;
}

/** A photo beyond the recommended list. */
export interface ExtraCard {
  kind: 'extra';
  photo: PhotoRef;
  /** 1-based, among the extras — "Photo 3". */
  ordinal: number;
  /** The card's BAND text — the photo's own label, else `Photo N Extra`. */
  title: string;
  /** True when the photo is parked (`Needs you`) — the band turns red. */
  parked: boolean;
  gone?: boolean;
}

/**
 * The empty hand (round 7 task 5) — no recommended shot list, no photo yet.
 * The design authority: *"If there aren't required pictures for a survey
 * section, the carousel should display a single empty picture labeled
 * 'Photo 1.'"* This is the ONLY card on a shelf that never carries a photo,
 * so tapping it always opens the picker scoped to the section itself (an
 * extra, never a shot) — `PhotoShelf.tsx` decides that, this module only
 * decides that the card exists.
 */
export interface PlaceholderCard {
  kind: 'placeholder';
  /** Always "Photo 1" — the one slot before anything has been filed. */
  title: string;
}

export type ShelfCard = ShotCard | ExtraCard | PlaceholderCard;

export interface ShelfModel {
  cards: ShelfCard[];
  /** Prompted recommended shots covered / wanted. */
  have: number;
  need: number;
  /** Extras beyond the shot list. */
  extra: number;
}

export interface ParkedInfo {
  reason: string;
  canRetry: boolean;
}

export function shelfModel(args: {
  doc: SurveyDocument;
  results: SurveyResults;
  section: SectionInstance;
  /** photoId → parked entry; a parked photo's card says "Needs you". */
  parkedPhotoIds: ReadonlySet<string> | ReadonlyMap<string, ParkedInfo>;
}): ShelfModel {
  const { doc, results, section } = args;
  const parked = args.parkedPhotoIds;
  const isParked = (id: string): boolean => (parked instanceof Map ? parked.has(id) : (parked as ReadonlySet<string>).has(id));
  const isGone = (id: string): boolean =>
    parked instanceof Map ? parked.get(id)?.canRetry === false : false;

  // Prompted = covered OR the engine still lists it missing. An un-prompted
  // conditional shot is neither, and is left off entirely.
  const missing = new Set(
    results.missingShots
      .filter((shot) => shot.sectionInstanceId === section.id)
      .map((shot) => shot.shotListId),
  );

  const covering = new Set<string>();
  const cards: ShelfCard[] = [];
  let have = 0;
  let need = 0;

  for (const shot of section.shotList) {
    const cover = doc.photos.find((photo) => photoCoversShot(photo, section, shot));
    if (!cover && !missing.has(shot.id)) continue;
    need += 1;
    // Amendment 8 (D-041): a per-car shot's caption lives under its BASE id —
    // one sentence covers every car's row.
    const note = FLS_SHOT_NOTES[shotBaseId(shot.id)];
    if (cover) {
      have += 1;
      covering.add(cover.id);
      cards.push({
        kind: 'shot',
        shotListId: shot.id,
        label: shot.label,
        ...(note ? { note } : {}),
        photo: cover,
        title: cover.label ?? shot.label,
        parked: isParked(cover.id),
        ...(isGone(cover.id) ? { gone: true } : {}),
      });
    } else {
      // Amendment 8 (D-041): a REQUIRED shot with nothing covering it yet
      // carries the card's own "Required" line — the surveyor's own written
      // reason, off the shot's own note, when there is one.
      const reason = shot.required ? shotReasonFor(doc, section.id, shot.id) : undefined;
      cards.push({
        kind: 'shot',
        shotListId: shot.id,
        label: shot.label,
        ...(note ? { note } : {}),
        title: shot.label,
        parked: false,
        ...(shot.required ? { required: true } : {}),
        ...(reason ? { reason } : {}),
      });
    }
  }

  // Extras: this section's own photos (no questionId) that cover no prompted
  // shot, in capture order — `doc.photos` is push order (store.addPhoto).
  let ordinal = 0;
  for (const photo of doc.photos) {
    if (photo.sectionInstanceId !== section.id) continue;
    if (photo.questionId !== undefined) continue;
    if (covering.has(photo.id)) continue;
    ordinal += 1;
    cards.push({
      kind: 'extra',
      photo,
      ordinal,
      title: photo.label ?? `Photo ${String(ordinal)} Extra`,
      parked: isParked(photo.id),
      ...(isGone(photo.id) ? { gone: true } : {}),
    });
  }

  // The empty hand (round 7 task 5, D-032 amendment): a section with no
  // prompted shot and no extra still gets the carousel — just one card, the
  // one waiting for a first photo, never zero cards and never nothing at all.
  if (cards.length === 0) {
    cards.push({ kind: 'placeholder', title: 'Photo 1' });
  }

  return { cards, have, need, extra: ordinal };
}
